import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ArticleIcon from "@mui/icons-material/Article";
import ReplyIcon from "@mui/icons-material/Reply";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MessageIcon from "@mui/icons-material/Message";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch, useSelector } from "react-redux";
import { getAuthToken } from "../../utils/getAuthToken";
import { addNodeToBranch, updateNodeInBranch } from "../../redux/Slice/Workflow/workflowSlice";
import axios from "axios";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import ReactTimezoneSelect from "react-timezone-select";
import { buildTimezoneSelectStyles } from "./timezoneSelectStyles";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

const MESSENGER_COLOR = "#0084FF";

// Facebook triggers – parity with Instagram: First message (with expiry), Comment on post, Automatic Reply, When contact closed
const FACEBOOK_TRIGGERS = [
  {
    id: "first-incoming-messenger",
    name: "First incoming Facebook message",
    description:
      "When someone sends their first message to your Facebook Page. You can add conditions (e.g. keywords) and set an expiry time.",
    triggerType: "root",
    category: "Message",
    config: [
      {
        name: "Conditions",
        fields: [
          {
            label: "Trigger when",
            type: "select",
            options: ["Any first message", "When message contains keywords"],
          },
          {
            label: "Keywords (comma-separated)",
            type: "text",
            description: "Optional. Only trigger when the first message contains one of these keywords.",
          },
        ],
      },
      {
        name: "Select Time Interval",
        fields: [
          {
            label: "Select Interval Duration",
            type: "number",
            description: "Workflow expires after this duration if no reply (0 = no expiry).",
          },
          {
            label: "Select Duration Type",
            type: "select",
            options: ["Minutes", "Hours", "Days"],
          },
          {
            label: "Timezone",
            type: "select",
            options: ["timezones"],
            description: "Timezone for expiry.",
          },
        ],
      },
    ],
  },
  {
    id: "comment-on-post",
    name: "Comment on post",
    description: "When someone comments on a post on your Facebook Page. Select the post and optionally filter by keywords.",
    triggerType: "root",
    category: "Message",
    config: [
      {
        name: "Post",
        fields: [
          {
            label: "Media ID",
            type: "select",
            description: "Run this workflow when someone comments on this post. Load posts from your connected Facebook Page.",
            options: [],
          },
        ],
      },
      {
        name: "Conditions",
        fields: [
          {
            label: "Trigger when",
            type: "select",
            options: ["Any comment", "When comment contains keywords"],
          },
          {
            label: "Keywords (comma-separated)",
            type: "text",
            description: "Optional. Only trigger when the comment contains one of these keywords.",
          },
        ],
      },
      {
        name: "Select Time Interval",
        fields: [
          {
            label: "Select Interval Duration",
            type: "number",
            description: "Workflow expires after this duration if no reply (0 = no expiry).",
          },
          {
            label: "Select Duration Type",
            type: "select",
            options: ["Minutes", "Hours", "Days"],
          },
          {
            label: "Timezone",
            type: "select",
            options: ["timezones"],
            description: "Timezone for expiry.",
          },
        ],
      },
    ],
  },
  {
    id: "automatic-reply-to-comment",
    name: "Automatic Reply to Comment",
    description: "When someone comments on a post, automatically reply with a message. Optionally filter by keywords.",
    triggerType: "root",
    category: "Message",
    config: [
      {
        name: "Post",
        fields: [
          {
            label: "Media ID",
            type: "select",
            description: "Auto-reply when someone comments on this post.",
            options: [],
          },
        ],
      },
      {
        name: "Conditions",
        fields: [
          {
            label: "Trigger when",
            type: "select",
            options: ["Any comment", "When comment contains keywords"],
          },
          {
            label: "Keywords (comma-separated)",
            type: "text",
            description: "Optional. Only auto-reply when the comment contains one of these keywords.",
          },
        ],
      },
      {
        name: "Reply",
        fields: [
          {
            label: "AI Agent",
            type: "select",
            description: "Optional. Generate the reply with AI instead of the fixed message below. Falls back to the message below if AI can't answer.",
          },
          {
            label: "Reply message",
            type: "textarea",
            description: "Text and/or emojis to send as a reply to the comment. Used as-is when AI is off, or as a fallback when AI is on.",
          },
        ],
      },
    ],
  },
  {
    id: "when-contact-closed",
    name: "When contact is closed",
    description: "When a teammate closes a Facebook conversation (marks it as done).",
    triggerType: "root",
    category: "Contact",
    config: [],
  },
];

const TRIGGER_ICONS = {
  "First incoming Facebook message": <ChatBubbleOutlineIcon sx={{ mr: 1, color: MESSENGER_COLOR }} />,
  "Comment on post": <ArticleIcon sx={{ mr: 1, color: MESSENGER_COLOR }} />,
  "Automatic Reply to Comment": <ReplyIcon sx={{ mr: 1, color: MESSENGER_COLOR }} />,
  "When contact is closed": <CheckCircleIcon sx={{ mr: 1, color: MESSENGER_COLOR }} />,
};

const FACEBOOK_CATEGORY_ICONS = {
  Message: <MessageIcon sx={{ mr: 1, color: MESSENGER_COLOR }} />,
  Contact: <PersonIcon sx={{ mr: 1, color: MESSENGER_COLOR }} />,
};

const ChooseFacebookTriggerDrawer = ({ open, onClose, editTrigger, branchId = "start" }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const { selectedWorkflow } = useSelector((state) => state.workflow);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [facebookPostsList, setFacebookPostsList] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [aiAgents, setAiAgents] = useState([]);
  const [aiAgentsLoading, setAiAgentsLoading] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const emojiPickerRef = useRef(null);
  const drawerBodyScrollRef = useRef(null);

  const triggerMuiMenuProps = {
    disablePortal: false,
    PaperProps: {
      sx: {
        zIndex: (t) => t.zIndex.modal + 2,
        bgcolor: isDark ? "#2d3136" : "#fff",
        maxHeight: 320,
      },
    },
  };
  const triggerMuiSelectSx = {
    mt: 0.5,
    borderRadius: "6px",
    fontSize: 15,
    bgcolor: isDark ? "#23272b" : "#fff",
    color: isDark ? "#fff" : "#222",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: isDark ? "#444" : "#ccc",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: isDark ? "#666" : "#999",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "primary.main",
    },
  };

  const triggersByCategory = React.useMemo(() => {
    const map = {};
    FACEBOOK_TRIGGERS.forEach((t) => {
      const cat = t.category || "Message";
      if (!map[cat]) map[cat] = [];
      map[cat].push(t);
    });
    return map;
  }, []);

  useEffect(() => {
    if (
      open &&
      (selectedTrigger?.name === "Comment on post" || selectedTrigger?.name === "Automatic Reply to Comment")
    ) {
      const workspaceId = selectedWorkflow?.workspaceId;
      if (!workspaceId) {
        setFacebookPostsList([]);
        return;
      }
      setLoadingPosts(true);
      getAuthToken()
        .then((token) => {
          if (!token) return Promise.reject(new Error("No token"));
          return axios.get(`${SERVER_DOMAIN}/api/workflow/facebook-posts`, {
            params: { workspaceId },
            headers: { Authorization: `Bearer ${token}` },
          });
        })
        .then((res) => {
          const data = res.data?.data || [];
          setFacebookPostsList(Array.isArray(data) ? data : []);
        })
        .catch((err) => {
          console.error("Failed to fetch Facebook posts:", err?.response?.data || err.message);
          setFacebookPostsList([]);
        })
        .finally(() => setLoadingPosts(false));
    } else {
      setFacebookPostsList([]);
    }
  }, [open, selectedTrigger?.name, selectedWorkflow?.workspaceId]);

  useEffect(() => {
    if (!open || selectedTrigger?.name !== "Automatic Reply to Comment") {
      setAiAgents([]);
      return;
    }
    const workspaceId = selectedWorkflow?.workspaceId;
    if (!workspaceId) {
      setAiAgents([]);
      return;
    }
    let cancelled = false;
    setAiAgentsLoading(true);
    (async () => {
      try {
        const token = await getAuthToken();
        if (cancelled || !token) return;
        const r = await fetch(`${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/agents`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!r.ok) {
          console.warn(`[AI Reply] Failed to load agents — HTTP ${r.status} for workspace ${workspaceId}`);
          return;
        }
        const data = await r.json();
        if (cancelled) return;
        const rawAgents = Array.isArray(data)
          ? data
          : Array.isArray(data?.agents)
            ? data.agents
            : Array.isArray(data?.data?.agents)
              ? data.data.agents
              : [];
        setAiAgents(rawAgents.filter((a) => a?.isActive !== false));
      } catch (err) {
        if (!cancelled) console.error(`[AI Reply] Error loading agents for workspace ${workspaceId}:`, err?.message || err);
      } finally {
        if (!cancelled) setAiAgentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, selectedTrigger?.name, selectedWorkflow?.workspaceId]);

  useEffect(() => {
    if (!emojiPickerOpen) return;
    const handleClickOutside = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setEmojiPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [emojiPickerOpen]);

  useEffect(() => {
    if (!open) setEmojiPickerOpen(false);
  }, [open]);

  useEffect(() => {
    if (open && editTrigger) {
      setSelectedTrigger(editTrigger);
      const newFormValues = {};
      (editTrigger.config || []).forEach((cfg) => {
        (cfg.fields || []).forEach((field) => {
          if (field.selectedValue !== undefined) {
            newFormValues[field.label] = field.selectedValue;
          } else if (
            field.type === "select" &&
            Array.isArray(field.options) &&
            field.options.length > 0
          ) {
            newFormValues[field.label] = field.options[0];
          } else if (field.type === "number") {
            newFormValues[field.label] = field.selectedValue ?? 0;
          } else if (field.options && Array.isArray(field.options) && field.options[0] === "timezones") {
            const tz = field.selectedValue;
            newFormValues[field.label] = typeof tz === "string" ? tz : (tz?.value ?? (Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"));
          } else {
            newFormValues[field.label] = "";
          }
        });
      });
      setFormValues(newFormValues);
    } else if (open && selectedTrigger) {
      const newFormValues = {};
      (selectedTrigger.config || []).forEach((cfg) => {
        (cfg.fields || []).forEach((field) => {
          if (
            field.type === "select" &&
            Array.isArray(field.options) &&
            field.options.length > 0
          ) {
            newFormValues[field.label] = field.options[0];
          } else if (field.type === "number") {
            newFormValues[field.label] = field.selectedValue ?? 0;
          } else if (field.options && Array.isArray(field.options) && field.options[0] === "timezones") {
            newFormValues[field.label] = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
          } else {
            newFormValues[field.label] = "";
          }
        });
      });
      setFormValues(newFormValues);
    }
    // Intentionally narrowed to `selectedTrigger?.name`: this effect also
    // calls setSelectedTrigger(editTrigger) above, and selectedTrigger.config
    // is read in the else branch, so depending on the full `selectedTrigger`
    // object risks a render loop. Only the trigger identity actually needs
    // to re-run this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editTrigger, selectedTrigger?.name]);

  useEffect(() => {
    if (!open) return;
    const el = drawerBodyScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [open, selectedTrigger, editTrigger]);

  const handleSaveTrigger = async () => {
    if (!selectedTrigger) return;

    const configWithSelectedValues = (selectedTrigger.config || []).map((cfg) => ({
      ...cfg,
      fields: (cfg.fields || []).map((field) => {
        const value = formValues[field.label];
        let selectedValue = value !== undefined ? value : "";
        if (field.type === "number") {
          const num = parseInt(value, 10);
          selectedValue = Number.isNaN(num) ? 0 : num;
        } else if (field.options && Array.isArray(field.options) && field.options[0] === "timezones") {
          selectedValue = typeof value === "object" && value?.value ? value.value : (value || "UTC");
        }
        return { ...field, selectedValue };
      }),
    }));

    const triggerWithConfig = {
      ...selectedTrigger,
      config: configWithSelectedValues,
    };

    setSaving(true);

    if (
      editTrigger &&
      editTrigger.branchId &&
      editTrigger.nodeId !== undefined &&
      editTrigger.nodeId !== null
    ) {
      await dispatch(
        updateNodeInBranch({
          branchId: editTrigger.branchId,
          nodeId: editTrigger.nodeId,
          updates: { trigger: triggerWithConfig },
        })
      );
    } else if (branchId) {
      const node = {
        id: Date.now().toString(),
        type: "trigger",
        trigger: triggerWithConfig,
      };
      await dispatch(addNodeToBranch({ branchId, node, nodeIndex: 0 }));
    } else {
      setSaving(false);
      return;
    }

    const finalState = window.store?.getState ? window.store.getState() : null;
    const finalWorkflow = finalState?.workflow?.selectedWorkflow;

    if (finalWorkflow && finalWorkflow._id) {
      const token = await getAuthToken();
      if (!token) {
        setSaving(false);
        return;
      }
      try {
        await axios.put(
          `${SERVER_DOMAIN}/api/workflow/update/${finalWorkflow._id}`,
          finalWorkflow,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSaving(false);
        if (onClose) onClose();
      } catch (err) {
        console.error("Backend update failed", err);
        setSaving(false);
      }
    } else {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        height: "100%",
        width: 360,
        bgcolor: isDark ? "#23272b" : "#fff",
        boxShadow: 6,
        zIndex: 30,
        borderLeft: `1.5px solid ${isDark ? "#444" : "#e0e0e0"}`,
        p: 1,
        display: "flex",
        flexDirection: "column",
        color: isDark ? theme.palette.text.primary : "inherit",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {selectedTrigger && (
            <IconButton
              onClick={() => setSelectedTrigger(null)}
              sx={{ mr: 1, color: isDark ? "#fff" : "#222" }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          )}
          <FlashOnIcon sx={{ color: MESSENGER_COLOR, fontSize: 28, mr: 1 }} />
          <Typography variant="h6" fontWeight={700}>
            Choose a Facebook trigger
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: isDark ? theme.palette.text.primary : undefined }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        ref={drawerBodyScrollRef}
        sx={{
          mt: 2,
          color: isDark ? theme.palette.text.secondary : "text.secondary",
          flex: 1,
          overflowY: "auto",
          pb: selectedTrigger ? 8 : 0,
          p: 1,
          scrollbarWidth: { xs: "none", sm: "auto" },
          msOverflowStyle: { xs: "none", sm: "auto" },
          "&::-webkit-scrollbar": {
            width: { xs: 0, sm: 8 },
            height: { xs: 0, sm: 8 },
          },
        }}
      >
        {selectedTrigger ? (
          <Box sx={{ position: "relative", minHeight: 300, pb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, color: isDark ? "#fff" : "#222" }}>
              {selectedTrigger.name}
            </Typography>

            {selectedTrigger.name === "When contact is closed" && (
              <Box sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: isDark ? "#3d2e00" : "#fff8e1",
                border: `1px solid ${isDark ? "#7a5c00" : "#ffe082"}`,
                color: isDark ? "#ffd54f" : "#7a5c00",
              }}>
                <Typography fontSize={13} fontWeight={500}>
                  ⚠️ This trigger will not run when the contact is outside the 24-hour messaging window. Messages cannot be sent after the window expires.
                </Typography>
              </Box>
            )}

            {Array.isArray(selectedTrigger.config) && selectedTrigger.config.length > 0 ? (
              selectedTrigger.config.map((cfg, idx) => (
                <Box key={idx} sx={{ mb: 3 }}>
                  <Typography
                    sx={{ fontWeight: 600, mb: 1, color: isDark ? "#fff" : "#222" }}
                  >
                    {cfg.name}
                  </Typography>
                  {Array.isArray(cfg.fields) &&
                    cfg.fields.map((field, fidx) => {
                      const isKeywordField = field.label === "Keywords (comma-separated)";
                      const triggerWhen = formValues["Trigger when"];
                      const showKeywordField =
                        !isKeywordField ||
                        (selectedTrigger?.name === "First incoming Facebook message" && triggerWhen === "When message contains keywords") ||
                        (selectedTrigger?.name === "Comment on post" && triggerWhen === "When comment contains keywords") ||
                        (selectedTrigger?.name === "Automatic Reply to Comment" && triggerWhen === "When comment contains keywords");
                      if (!showKeywordField) return null;
                      return (
                      <Box key={fidx} sx={{ mb: 2 }}>
                        <label
                          style={{
                            color: isDark ? "#bbb" : "#555",
                            fontSize: 14,
                          }}
                        >
                          {field.label}
                        </label>
                        {field.description && (
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ color: isDark ? "#888" : "#666", mb: 0.5 }}
                          >
                            {field.description}
                          </Typography>
                        )}
                        {field.label === "AI Agent" && selectedTrigger?.name === "Automatic Reply to Comment" ? (
                          <FormControl size="small" sx={{ mt: 1, mb: 1, width: "90%", maxWidth: "100%" }}>
                            <Select
                              value={formValues[field.label] || ""}
                              onChange={(e) =>
                                setFormValues((v) => ({ ...v, [field.label]: e.target.value }))
                              }
                              displayEmpty
                              renderValue={(val) => {
                                if (!val) return "Off — use fixed reply message";
                                if (val === "__default__") return "AI — workspace default agent";
                                const agent = aiAgents.find((a) => a._id === val);
                                return agent ? `AI — ${agent.name}` : "AI — workspace default agent";
                              }}
                              MenuProps={triggerMuiMenuProps}
                              sx={triggerMuiSelectSx}
                            >
                              <MenuItem value="" sx={{ fontSize: 15 }}>
                                Off — use fixed reply message
                              </MenuItem>
                              <MenuItem value="__default__" sx={{ fontSize: 15 }}>
                                AI — workspace default agent
                              </MenuItem>
                              {aiAgentsLoading && (
                                <MenuItem disabled sx={{ fontSize: 13, opacity: 0.6 }}>
                                  Loading agents…
                                </MenuItem>
                              )}
                              {!aiAgentsLoading &&
                                aiAgents.map((agent) => (
                                  <MenuItem key={agent._id} value={agent._id} sx={{ fontSize: 15 }}>
                                    AI — {agent.name}
                                    {agent.isDefault ? " (default)" : ""}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        ) : field.options && Array.isArray(field.options) && field.options[0] === "timezones" ? (
                          <Box sx={{ mt: 1, mb: 2, width: "90%" }}>
                            <ReactTimezoneSelect
                              value={
                                typeof formValues[field.label] === "object" && formValues[field.label]?.value
                                  ? formValues[field.label]
                                  : formValues[field.label] || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
                              }
                              onChange={(tz) =>
                                setFormValues((v) => ({ ...v, [field.label]: tz }))
                              }
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={buildTimezoneSelectStyles(isDark, {
                                controlAndMenuWidth: "90%",
                                menuPortalZIndex: 9999,
                              })}
                            />
                          </Box>
                        ) : field.type === "select" &&
                          (field.label === "Media ID" &&
                          (selectedTrigger?.name === "Comment on post" || selectedTrigger?.name === "Automatic Reply to Comment")
                            ? true
                            : Array.isArray(field.options) && field.options.length > 0) ? (
                          field.label === "Media ID" &&
                          (selectedTrigger?.name === "Comment on post" || selectedTrigger?.name === "Automatic Reply to Comment") ? (
                            <Box sx={{ mt: 1, mb: 1 }}>
                              {loadingPosts ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <CircularProgress size={20} />
                                  <Typography variant="body2" color="text.secondary">
                                    Loading posts…
                                  </Typography>
                                </Box>
                              ) : (
                                <FormControl size="small" sx={{ mt: 1, mb: 1, width: "90%", maxWidth: "100%" }}>
                                  <Select
                                    displayEmpty
                                    value={(() => {
                                      const v = formValues[field.label];
                                      const allowed = new Set([
                                        "",
                                        "__any__",
                                        ...facebookPostsList.map((i) => String(i.id)),
                                      ]);
                                      return v !== undefined && v !== null && allowed.has(String(v))
                                        ? String(v)
                                        : "";
                                    })()}
                                    onChange={(e) =>
                                      setFormValues((v) => ({ ...v, [field.label]: e.target.value }))
                                    }
                                    renderValue={(val) => {
                                      if (!val) {
                                        return (
                                          <Typography component="span" sx={{ opacity: 0.7, fontSize: 15 }}>
                                            Select a post
                                          </Typography>
                                        );
                                      }
                                      if (val === "__any__") {
                                        return "Any post (run on any comment on any post)";
                                      }
                                      const item = facebookPostsList.find((i) => String(i.id) === String(val));
                                      if (!item) return val;
                                      const hasText = item.message && String(item.message).trim();
                                      const raw = hasText ? item.message : `Post – ${item.id}`;
                                      const text = typeof raw === "string" ? raw : String(raw);
                                      return text.length > 50 ? `${text.slice(0, 50)}…` : text;
                                    }}
                                    MenuProps={triggerMuiMenuProps}
                                    sx={triggerMuiSelectSx}
                                  >
                                    <MenuItem value="" sx={{ fontSize: 15 }}>
                                      <em style={{ color: isDark ? "#888" : "#666" }}>Select a post</em>
                                    </MenuItem>
                                    <MenuItem value="__any__" sx={{ fontSize: 15 }}>
                                      Any post (run on any comment on any post)
                                    </MenuItem>
                                    {facebookPostsList.map((item) => {
                                      const hasText = item.message && String(item.message).trim();
                                      const raw = hasText ? item.message : `Post – ${item.id}`;
                                      const label =
                                        typeof raw === "string" && raw.length > 50
                                          ? `${raw.slice(0, 50)}…`
                                          : raw;
                                      return (
                                        <MenuItem key={item.id} value={String(item.id)} sx={{ fontSize: 15 }}>
                                          {label}
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
                              )}
                            </Box>
                          ) : (
                            <FormControl size="small" sx={{ mt: 1, mb: 1, width: "90%", maxWidth: "100%" }}>
                              <Select
                                value={
                                  field.options.includes(formValues[field.label])
                                    ? formValues[field.label]
                                    : field.options[0] || ""
                                }
                                onChange={(e) =>
                                  setFormValues((v) => ({ ...v, [field.label]: e.target.value }))
                                }
                                MenuProps={triggerMuiMenuProps}
                                sx={triggerMuiSelectSx}
                              >
                                {field.options.map((opt, i) => (
                                  <MenuItem key={i} value={opt} sx={{ fontSize: 15 }}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )
                        ) : field.type === "number" ? (
                          <input
                            type="number"
                            min={0}
                            value={formValues[field.label] ?? ""}
                            onChange={(e) =>
                              setFormValues((v) => ({ ...v, [field.label]: e.target.value }))
                            }
                            style={{
                              width: "90%",
                              marginTop: 4,
                              padding: "8px 10px",
                              borderRadius: 6,
                              border: `1px solid ${isDark ? "#444" : "#ccc"}`,
                              background: isDark ? "#23272b" : "#fff",
                              color: isDark ? "#fff" : "#222",
                              fontSize: 15,
                              marginBottom: 8,
                            }}
                          />
                        ) : field.label === "Reply message" &&
                          selectedTrigger?.name === "Automatic Reply to Comment" ? (
                          <Box sx={{ position: "relative", width: "90%", mt: 1, mb: 1 }} ref={emojiPickerRef}>
                            <textarea
                              value={formValues[field.label] || ""}
                              onChange={(e) =>
                                setFormValues((v) => ({ ...v, [field.label]: e.target.value }))
                              }
                              placeholder={field.description || ""}
                              rows={3}
                              style={{
                                width: "100%",
                                padding: "8px 10px 8px 36px",
                                borderRadius: 6,
                                border: `1px solid ${isDark ? "#444" : "#ccc"}`,
                                background: isDark ? "#23272b" : "#fff",
                                color: isDark ? "#fff" : "#222",
                                fontSize: 15,
                                resize: "vertical",
                                boxSizing: "border-box",
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => setEmojiPickerOpen((prev) => !prev)}
                              sx={{
                                position: "absolute",
                                left: 4,
                                top: 8,
                                color: isDark ? "#aaa" : "#666",
                              }}
                              title="Add emoji"
                            >
                              <EmojiEmotionsIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                            {emojiPickerOpen && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: "100%",
                                  left: 0,
                                  mb: 0.5,
                                  zIndex: 20,
                                  boxShadow: 2,
                                  borderRadius: 1,
                                }}
                              >
                                <Picker
                                  data={data}
                                  onEmojiSelect={(emoji) => {
                                    setFormValues((v) => ({
                                      ...v,
                                      [field.label]: (v[field.label] || "") + (emoji.native || ""),
                                    }));
                                  }}
                                  theme={theme.palette.mode}
                                />
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <input
                            type="text"
                            value={formValues[field.label] || ""}
                            onChange={(e) =>
                              setFormValues((v) => ({ ...v, [field.label]: e.target.value }))
                            }
                            placeholder={field.description || ""}
                            style={{
                              width: "90%",
                              marginTop: 4,
                              padding: "8px 10px",
                              borderRadius: 6,
                              border: `1px solid ${isDark ? "#444" : "#ccc"}`,
                              background: isDark ? "#23272b" : "#fff",
                              color: isDark ? "#fff" : "#222",
                              fontSize: 15,
                              marginBottom: 8,
                            }}
                          />
                        )}
                      </Box>
                    ); })}
                </Box>
              ))
            ) : (
              <Typography sx={{ color: isDark ? "#aaa" : "#666", fontSize: 14 }}>
                No additional settings. Save to use this trigger.
              </Typography>
            )}

            <Box
              sx={{
                position: "relative",
                width: "100%",
                zIndex: 40,
                px: 2,
                background: isDark ? "#23272b" : "#fff",
                borderTop: `1px solid ${isDark ? "#444" : "#e0e0e0"}`,
                display: "flex",
                justifyContent: "flex-start",
                py: 2,
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  borderRadius: "20px",
                  backgroundColor: MESSENGER_COLOR,
                  color: "white",
                  textTransform: "capitalize",
                  padding: "8px 16px",
                  fontWeight: 600,
                  fontSize: 16,
                  boxShadow: 2,
                  "&:hover": { backgroundColor: "#0066CC" },
                }}
                onClick={handleSaveTrigger}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Box>
          </Box>
        ) : (
          ["Message", "Contact"].map((category) => {
            const triggers = triggersByCategory[category] || [];
            if (triggers.length === 0) return null;
            return (
              <Accordion
                key={category}
                expanded={expandedAccordion === category}
                onChange={(e, isExpanded) => setExpandedAccordion(isExpanded ? category : null)}
                sx={{
                  bgcolor: isDark ? "#23272b" : "#f5f5f5",
                  color: isDark ? theme.palette.text.primary : theme.palette.text.primary,
                  mb: 1,
                  boxShadow: 0,
                  border: `1px solid ${isDark ? "#444" : "#e0e0e0"}`,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {FACEBOOK_CATEGORY_ICONS[category] || (
                      <FlashOnIcon sx={{ mr: 1, color: MESSENGER_COLOR }} />
                    )}
                    <Typography fontWeight={600}>{category}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0 }}>
                  {triggers.map((trigger) => (
            <Paper
              key={trigger.id}
              elevation={isDark ? 2 : 1}
              sx={{
                mb: 2,
                p: 2,
                bgcolor: isDark ? "#23272b" : "#fff",
                border: `1px solid ${isDark ? "#444" : "#e0e0e0"}`,
                transition: "box-shadow 0.2s, border-color 0.2s",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: 4,
                  borderColor: MESSENGER_COLOR,
                  background: isDark ? "#2e3440" : "#e7f3ff",
                },
              }}
              onClick={() => {
                setSelectedTrigger(trigger);
                const initial = {};
                (trigger.config || []).forEach((cfg) => {
                  (cfg.fields || []).forEach((field) => {
                    if (
                      field.type === "select" &&
                      Array.isArray(field.options) &&
                      field.options.length > 0
                    ) {
                      initial[field.label] = field.options[0];
                            } else if (field.options && Array.isArray(field.options) && field.options[0] === "timezones") {
                              initial[field.label] = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
                            } else if (field.type === "number") {
                              initial[field.label] = 0;
                    } else {
                      initial[field.label] = "";
                    }
                  });
                });
                setFormValues(initial);
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                {TRIGGER_ICONS[trigger.name] || (
                  <FlashOnIcon sx={{ mr: 1, color: MESSENGER_COLOR }} />
                )}
                <Box>
                  <Typography fontWeight={700} fontSize={16} gutterBottom>
                    {trigger.name}
                  </Typography>
                  <Typography
                    fontSize={14}
                    color={isDark ? theme.palette.text.secondary : "text.secondary"}
                  >
                    {trigger.description || "No description"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default ChooseFacebookTriggerDrawer;
